import { Button, Col, Form, Input, InputNumber, Modal, Row, Typography, message } from "antd";
import FormItem from "antd/es/form/FormItem";
import { useEffect, useState } from "react";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { DataTable, ManualEmissionTank, RowData, TankEmission, TankManual } from "src/entity/tank/tank.entity";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { AreaProps } from "src/entity/company/company-response.entity";
import { ReportStatusEnum } from "src/entity/voc-approve/voc-approve.entity";
import { formatNumber } from "src/utils/process-number";
import { SettingEmissionControlTank, SettingTankType } from "src/entity/settings/tank";
const { Text } = Typography;

const ManualTankModal = (props: {
  open: boolean;
  rawData: TankManual;
  dataTable: DataTable;
  areaMaster: AreaProps[];
  tankTypeMaster: SettingTankType[];
  tankEmissionControlMaster: SettingEmissionControlTank[];
  loading: boolean;
  onCancel: () => void;
  onSave: (data: TankManual) => void;
}) => {
  const { open, loading, rawData, dataTable, areaMaster, tankTypeMaster, tankEmissionControlMaster, onCancel, onSave } = props;
  const [tankData, setTankData] = useState<TankManual>(rawData);
  const [messageApi, contextHolder] = message.useMessage();

  // Update tankData when rawData prop changes
  useEffect(() => {
    setTankData(rawData);
  }, [rawData]);

  type MonthManual = keyof ManualEmissionTank;
  const months: MonthManual[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

  const staticColumnsKey = [
    {
      label: "Tank ID",
      key: "tankId",
    },
    {
      label: "Area",
      key: "area",
    },
    {
      label: "Chemical Name",
      key: "chemicalName",
    },
    {
      label: "Tank Type",
      key: "tankType",
    },
  ];

  const checkStatusReport = (month: keyof ManualEmissionTank | undefined) => {
    let isDisabled = false;
    if (month) {
      if (tankData.manualEmissionTank[month].reportStatus == ReportStatusEnum.PROCESSING) {
        isDisabled = false;
      } else {
        isDisabled = true;
      }
    }
    return isDisabled;
  };

  const handleSaveManualTank = async () => {
    try {
      await onSave(tankData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleInputChange = (
    parameter: keyof TankEmission,
    month: keyof ManualEmissionTank,
    value: number,
    type: "emission" | "emissionControl",
  ) => {
    const tankParam: keyof TankEmission = parameter.toLocaleLowerCase() as keyof TankEmission;

    const tmpDataTable: RowData[] = dataTable;
    const indexDataTable = tmpDataTable.findIndex((el) => el.parameter === parameter);

    // validate value if type === "emissionControl" should sum less than or equal 100
    if (type === "emissionControl") {
      let currentValue = value;
      const otherEmissionControl = tmpDataTable
        .filter((item) => item.type === "emissionControl" && item.parameter !== parameter)
        .reduce((sum, item) => sum + (item[month] ?? 0), 0);
      // console.log({ month, currentValue, otherEmissionControl });
      if (otherEmissionControl + currentValue > 100) {
        messageApi.open({
          type: "error",
          content: "The total Emission Control value cannot exceed 100%.",
          duration: 4,
        });
        value = Math.abs(otherEmissionControl - 100);
      }
    }
    if (indexDataTable != -1 && tmpDataTable[indexDataTable]) {
      tmpDataTable[indexDataTable][month] = value;
    }

    if (type === "emissionControl") {
      const indexEmissionControl = tankData.emissionControlDetails.findIndex(
        (item) => item.emissionControlId.toString() === parameter.toString(),
      );
      if (indexEmissionControl !== -1) {
        setTankData((prevData) => ({
          ...prevData,
          emissionControlDetails: prevData.emissionControlDetails.map((detail, idx) =>
            idx === indexEmissionControl
              ? {
                  ...detail,
                  unit: detail.unit ?? "%",
                  monthlyData: {
                    jan: { value: 0 },
                    feb: { value: 0 },
                    mar: { value: 0 },
                    apr: { value: 0 },
                    may: { value: 0 },
                    jun: { value: 0 },
                    jul: { value: 0 },
                    aug: { value: 0 },
                    sep: { value: 0 },
                    oct: { value: 0 },
                    nov: { value: 0 },
                    dec: { value: 0 },
                    ...detail.monthlyData,
                    [month]: { value: value },
                  },
                }
              : detail,
          ),
        }));
      }
    } else {
      setTankData((prevData) => ({
        ...prevData,
        manualEmissionTank: {
          ...prevData.manualEmissionTank,
          [month]: {
            ...prevData.manualEmissionTank[month],
            emission: {
              ...prevData.manualEmissionTank[month].emission,
              [tankParam]: {
                ...prevData.manualEmissionTank[month].emission[tankParam],
                value: value,
              },
            },
          },
        },
      }));
    }
  };

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return String(optValue?.areaName);
    } else if (type === "tankTypeId") {
      const optValue = tankTypeMaster.find((item) => item._id === text);
      return optValue?.tankType;
    } else if (type === "emissionControlId" && tankEmissionControlMaster) {
      const optValue = tankEmissionControlMaster.find((item) => item._id === text);
      return optValue?.emissionControl;
    } else if (type === "efficiency") {
      const optValue = tankEmissionControlMaster.find((item) => item._id === text);
      return optValue?.efficiency;
    } else {
      return text;
    }
  };

  return (
    <div>
      {contextHolder}
      <Modal
        title={<ModalTitle title="VOCs Tank 4.0" />}
        open={open}
        width={1200}
        okText="Save"
        onCancel={onCancel}
        cancelText="Cancel"
        centered
        bodyStyle={{ height: 600, overflowY: "auto", overflowX: "hidden" }}
        maskClosable={false}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" onClick={handleSaveManualTank} loading={loading}>
              Save
            </Button>
          </Row>
        }
      >
        <Form size="large" layout="vertical" autoComplete="off">
          <div className="pl-20 pr-20 pt-10">
            <Row gutter={16}>
              {staticColumnsKey.map((column, index) => (
                <Col xs={{ span: 24 }} sm={{ span: 8 }} md={{ span: 8 }} key={column.key}>
                  <FormItem label={column.label}>
                    <Input disabled value={getMasterLabel(column.key, String(tankData[column.key as keyof TankManual])) ?? ""} />
                  </FormItem>
                </Col>
              ))}

              <Col xs={{ span: 24 }} sm={{ span: 8 }} md={{ span: 8 }} key={"emissionControl"}>
                <FormItem label={"Emission Control"}>
                  <Input
                    disabled
                    value={tankData.emissionControlDetails
                      .map((emissionDetail) => getMasterLabel("emissionControlId", emissionDetail.emissionControlId))
                      .join(" / ")}
                  />
                </FormItem>
              </Col>
              <Col xs={{ span: 24 }} sm={{ span: 8 }} md={{ span: 8 }} key={"efficiency"}>
                <FormItem label={"Efficiency (%)"}>
                  <Input
                    disabled
                    value={tankData.emissionControlDetails
                      .map((emissionDetail) => getMasterLabel("efficiency", emissionDetail.emissionControlId))
                      .join(" / ")}
                  />
                </FormItem>
              </Col>
            </Row>
          </div>
          <div className="pt-5 pb-5">
            <Text style={{ fontWeight: "bold", textAlign: "center", display: "block" }}>Emission Before Treatment</Text>
          </div>
          <div className="pl-10 pr-10">
            <Row style={{ justifyContent: "center", alignItems: "center", paddingBottom: "25px" }}>
              <Col span={3}>
                <Row gutter={[0, 8]}>
                  <Col span={16}></Col>
                  <Col span={8}>Unit</Col>
                </Row>
              </Col>
              <Col span={21}>
                <Row gutter={[0, 8]}>
                  {months.map((month) => (
                    <>
                      <Col span={2} style={{ textAlign: "center" }}>
                        {month.charAt(0).toUpperCase() + month.slice(1)}
                      </Col>
                    </>
                  ))}
                </Row>
              </Col>
            </Row>

            {dataTable.map((row, rowIndex) => (
              <div key={rowIndex}>
                <Row gutter={[0, 0]} style={{ display: "flex", justifyContent: "center", alignItems: "baseline" }}>
                  <Col span={3}>
                    <Row gutter={[0, 0]}>
                      <Col span={16}>
                        <Form.Item name={["items", rowIndex, "parameter"]}>
                          {row.type === "emission" ? (
                            <p>{row.parameter === "TVOC" ? "TVOCs" : row.parameter}</p>
                          ) : (
                            <p>{getMasterLabel("emissionControlId", row.parameter ?? "")}</p>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item>
                          <p>{row.unit}</p>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>

                  <Col span={21}>
                    <Row gutter={[8, 0]}>
                      {months.map((month, monthIndex) => (
                        <Col span={2} key={`${monthIndex}-${row.key}-${month}`}>
                          <InputNumber
                            min={0}
                            max={row.type === "emissionControl" ? 100 : undefined}
                            size="large"
                            step={1}
                            style={{ width: "100%", fontSize: "10px" }}
                            disabled={checkStatusReport(month)}
                            value={row[month]}
                            onChange={(value) => {
                              handleInputChange(
                                row.parameter as keyof TankEmission,
                                month,
                                value ?? 0,
                                row.type as "emission" | "emissionControl",
                              );
                            }}
                            onKeyDown={(e) => {
                              onKeyDownNumberWithFixedDecimal(e, 2);
                            }}
                            formatter={(value) => formatNumber(value)}
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ManualTankModal;
